import { useState } from 'react';

import { MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { OperationItem, useGetApiPlacementOperations } from '@/api';
import noOperationPng from '@/assets/images/noOperation.png';
import noOperationWebp from '@/assets/images/noOperation.webp';
import ChipsRadioSmall from '@/components/chipsRadioSmall/chipsRadioSmall';
import DemoTag from '@/components/DemoTag/DemoTag';
import Image from '@/components/Image/Image';
import { Loader } from '@/components/Loader/loader';
import { RowOperation } from '@/components/RowOperation/RowOperation';
import { ROUTE } from '@/constants/route';
import { useDemoContext } from '@/hooks/DemoContext';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';

import styles from './styles.module.css';

const operationsBoutonFiltre = [
  {
    label: 'Dépôt',
    value: 'Versement',
    tracking: {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'depots',
      event_detail_1: 'cta',
    },
  },
  {
    label: 'Retrait',
    value: 'Rachat',
    tracking: {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'retraits',
      event_detail_1: 'cta',
    },
  },
  {
    label: 'Frais',
    value: 'Frais',
    tracking: {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'frais',
      event_detail_1: 'cta',
    },
  },
  {
    label: 'Tous',
    value: '',
    tracking: {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'toutes les operations',
      event_detail_1: 'cta',
    },
  },
];

interface OperationContratProps {
  data?: OperationItem[] | null | undefined;
  placementId?: string | null | undefined;
}

export const OperationContrat: React.FC<OperationContratProps> = ({ data, placementId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDemo } = useDemoContext();
  const [filterOperations, setFilterOperations] = useState('');
  const { data: allOperations, isLoading, isError } = useGetApiPlacementOperations(placementId ?? '');

  const filteredData = allOperations?.operations?.filter((operation) =>
    filterOperations ? operation?.typeOperation?.toLowerCase()?.includes(filterOperations.toLowerCase()) : true
  );
  const filteredDataWithKeys = filteredData?.map((operation, index) => ({
    ...operation,
    key: index,
  }));

  if (isError) navigate(ROUTE.ERREUR, { state: { goTo: ROUTE.CONTRATS } });

  if (isLoading) return <Loader />;
  return (
    <div className={styles.container}>
      <h2 className={styles.contractDetailsSectionTitle}>
        {t('common.movements')}
        {isDemo && <DemoTag />}
      </h2>
      <div className={styles.lastOperations}>
        <MtxGrid row>
          <MtxGrid col colXs={12} colSm={12} colMd={12} colLg={7} colXl={7}>
            <div className={styles.filterButton}>
              {operationsBoutonFiltre.map((value, index, buttonsFiltre) => (
                <ChipsRadioSmall
                  key={value.value}
                  label={value.label}
                  name="dernieres-operations-chips"
                  value={value.value}
                  defaultCheck={index + 1 === buttonsFiltre.length}
                  onClick={() => {
                    setFilterOperations(value.value);
                    void TRACKING_INSTANCE.triggerEvent('clic', undefined, value.tracking);
                  }}
                />
              ))}
            </div>

            {data ? (
              <table className={styles.operationsTable}>
                <tbody className="list-operations" id="list-operations">
                  {filteredDataWithKeys && filteredDataWithKeys.length > 0 ? (
                    filteredDataWithKeys.map((operation) => (
                      <RowOperation
                        key={operation.key}
                        typeOperation={operation.typeOperation ?? ''}
                        dateOperation={operation.dateOperation ?? ''}
                        montantOperation={operation.montantOperation ?? 0}
                      />
                    ))
                  ) : (
                    <div className={styles.emptyData}>
                      <Image
                        defaultSrc={noOperationPng}
                        webpSrc={noOperationWebp}
                        alt="Image Aucune opération"
                        className="m-auto"
                      />
                      <p>{t('common.noTransaction')}</p>
                    </div>
                  )}
                </tbody>
              </table>
            ) : (
              <div className={styles.emptyData}>
                <Image
                  defaultSrc={noOperationPng}
                  webpSrc={noOperationWebp}
                  alt="Image Aucune opération"
                  className="m-auto"
                />
                <p>Aucune opération</p>
              </div>
            )}
          </MtxGrid>
        </MtxGrid>
      </div>
    </div>
  );
};
